import React from 'react';
import { Mail, Phone, MapPin, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import LogoHeader from '../../assets/logo-header.svg';
import { handleEmailClick } from '../../utils/emailHandler';

export default function Footer() {
  return (
    <footer className="relative border-t border-white/5 bg-[#050508] pt-14 md:pt-20 pb-8 md:pb-10 px-4 md:px-6 overflow-hidden z-20">

      {/* Running Animated RGB bottom neon line */}
      <div className="absolute top-0 left-0 w-full h-[1.5px] bg-gradient-to-r from-cyber-blue via-cyber-purple to-cyber-pink"></div>

      {/* Floating ambient glow lights */}
      <div className="absolute bottom-[-10%] right-[10%] w-[300px] h-[300px] bg-cyber-blue/5 rounded-full blur-[90px] pointer-events-none"></div>
      <div className="absolute top-[20%] left-[5%] w-[250px] h-[250px] bg-cyber-purple/5 rounded-full blur-[80px] pointer-events-none"></div>

      <div className="container mx-auto relative z-10">

        <div className="grid grid-cols-2 lg:grid-cols-12 gap-6 md:gap-12 pb-4 md:pb-16">

          {/* Column 1: Brand Logo & Mission */}
          <div className="col-span-2 md:col-span-1 lg:col-span-3 space-y-6 order-1">
            <div className="flex items-center justify-center md:justify-start">
              <div className="h-16 flex items-center justify-center">
                <img
                  src={LogoHeader}
                  alt="CUBE GAMES"
                  className="h-full w-auto object-contain filter"
                />
              </div>
            </div>

            <div className="space-y-3 text-center md:text-start">
              <h4 className="text-cyber-blue font-cyber font-semibold text-lg tracking-tight">Level Up Your Library</h4>
              <p className="text-gray-500 text-sm max-w-sm leading-relaxed font-sans font-light">
                Buy and download games instantly from our online gaming store.
              </p>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="col-span-1 md:col-span-1 lg:col-span-2 space-y-6 order-2 md:order-3 lg:order-2 text-center md:text-start">
            <h4 className="font-cyber font-semibold text-xs md:text-sm uppercase tracking-widest text-white border-b border-white/10 pb-2 inline-block whitespace-nowrap">Quick Links</h4>
            <ul className="space-y-3 text-sm text-gray-400 font-sans font-light">
              <li>
                <Link to="/" className="flex items-center justify-center md:justify-start gap-2 hover:text-cyber-purple transition-colors group">
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all" />
                  Home
                </Link>
              </li>
              <li>
                <Link to="/all-games" className="flex items-center justify-center md:justify-start gap-2 hover:text-cyber-purple transition-colors group">
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all" />
                  Games
                </Link>
              </li>
              <li>
                <Link to="/platforms" className="flex items-center justify-center md:justify-start gap-2 hover:text-cyber-purple transition-colors group">
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all" />
                  Platforms
                </Link>
              </li>
              <li>
                <Link to="/contact" className="flex items-center justify-center md:justify-start gap-2 hover:text-cyber-purple transition-colors group">
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all" />
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/blog" className="flex items-center justify-center md:justify-start gap-2 hover:text-cyber-purple transition-colors group">
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all" />
                  Blogs
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Support */}
          <div className="col-span-1 md:col-span-1 lg:col-span-3 space-y-6 order-3 md:order-4 lg:order-3 text-center md:text-start">
            <h4 className="font-cyber font-semibold text-xs sm:text-sm uppercase tracking-widest text-white border-b border-white/10 pb-2 inline-block">Support</h4>
            <ul className="space-y-3 text-sm text-gray-400 font-sans font-light">
              <li>
                <Link to="/faq" className="flex items-center justify-center md:justify-start gap-2 hover:text-cyber-blue transition-colors group">
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all" />
                  FAQs
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="flex items-center justify-center md:justify-start gap-2 hover:text-cyber-blue transition-colors group">
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all" />
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="flex items-center justify-center md:justify-start gap-2 hover:text-cyber-blue transition-colors group">
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all" />
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact Info */}
          <div className="col-span-2 md:col-span-1 lg:col-span-4 space-y-6 order-4 md:order-2 lg:order-4 text-center md:text-start">
            <h4 className="font-cyber font-semibold text-sm uppercase tracking-widest text-white border-b border-white/10 pb-2 inline-block">Contact Info</h4>
            <div className="space-y-5">
              <div className="flex items-center justify-center md:justify-start gap-4 group">
                <div className="hidden md:block p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-cyber-purple transition-all">
                  <Mail className="w-4 h-4 text-cyber-purple" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 font-cyber font-medium uppercase tracking-widest mb-1">Email us</p>
                  <a href="mailto:info@cubegames.ca" onClick={handleEmailClick} className="text-sm text-gray-300 hover:text-white transition-colors font-sans">info@cubegames.ca</a>
                </div>
              </div>

              <div className="flex items-center justify-center md:justify-start gap-4 group">
                <div className="hidden md:block p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-cyber-blue transition-all">
                  <Phone className="w-4 h-4 text-cyber-blue" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 font-cyber font-medium uppercase tracking-widest mb-1">Call us</p>
                  <a href="tel:1800-9797-6361" className="text-sm text-gray-300 font-sans">1800-9797-6361</a>
                  <br />
                  <a href="tel:1800-9797-9055" className="text-sm text-gray-300 font-sans">1800-9797-9055</a>
                </div>
              </div>

              <div className="flex items-center justify-center md:justify-start gap-4 group">
                <div className="hidden md:block p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-cyber-purple transition-all">
                  <MapPin className="w-4 h-4 text-cyber-purple" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 font-cyber font-medium uppercase tracking-widest mb-1">Location</p>
                  <a href="https://www.google.com/maps/search/?api=1&query=124 Alison Ave Cambridge, ON N1R 1N4 Canada" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 font-sans leading-relaxed">
                    124 Alison Ave Cambridge, ON N1R 1N4 Canada
                  </a>
                  {/* <p className="text-[10px] text-gray-500 font-sans mt-2 italic leading-relaxed">
                    456 Neon Avenue, Tech Hub, Innovation Sector (Branch)
                  </p> */}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom copyright alignment */}
        <div className="pt-8 border-t border-white/5 flex items-center justify-center text-[9px] md:text-xs text-gray-600 gap-4 font-mono ">
          <p>© 2026 CUBE GAMES. All rights reserved by YOYO GAMES INC.</p>
        </div>
      </div>
    </footer>
  );
}
